import React, { useEffect, useState } from 'react';
import { X, Share, PlusSquare, Download, Smartphone } from 'lucide-react';
import { Button } from './ui/button';
import { useRGPDGamification } from '../hooks/useRGPDGamification';

const DISMISS_KEY = 'fdn_pwa_dismissed_at';
const DISMISS_DAYS = 7;

function isIos() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

function recentlyDismissed() {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return ts && (Date.now() - ts) < DISMISS_DAYS * 24 * 3600 * 1000;
  } catch { return false; }
}

export default function InstallPrompt() {
  const { state: rgpd } = useRGPDGamification();
  const [deferred, setDeferred] = useState(null);
  const [mode, setMode] = useState(null); // 'android' | 'ios' | null

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return undefined;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setMode('android');
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    let iosTimer;
    if (isIos()) {
      iosTimer = setTimeout(() => setMode('ios'), 2500);
    }
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    setMode(null);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      const { outcome } = await deferred.userChoice;
      if (outcome === 'accepted') setMode(null);
    } catch { /* noop */ }
    setDeferred(null);
  };

  if (!mode || !rgpd.consent) return null; // attend la réponse au bandeau RGPD (évite la superposition)

  return (
    <div data-testid="pwa-install-banner" className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-[400px] z-[9990] rounded-2xl bg-[#1a1d1a] text-white shadow-2xl border border-white/10 p-4 animate-in slide-in-from-bottom-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-white text-[#1a1d1a] flex items-center justify-center font-bold text-lg shrink-0">F</div>
        <div className="flex-1 min-w-0">
          {mode === 'android' ? (
            <>
              <div className="text-[14px] font-semibold flex items-center gap-1.5">
                <Smartphone className="w-4 h-4 text-[#d97757]" /> Installez Fodindin Cagnotte sur votre téléphone
              </div>
              <p className="mt-1 text-[12px] text-white/70 leading-relaxed">Accès plus rapide, hors-ligne partiel, comme une vraie application !</p>
              <div className="mt-3 flex gap-2">
                <Button data-testid="pwa-install-btn" onClick={install} className="btn-anim h-9 rounded-full px-4 bg-[#d97757] hover:bg-[#c66745] text-white text-[12.5px] font-semibold">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Installer maintenant
                </Button>
                <Button data-testid="pwa-later-btn" onClick={dismiss} variant="ghost" className="h-9 rounded-full px-4 text-white/70 hover:text-white hover:bg-white/10 text-[12.5px]">
                  Plus tard
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="text-[14px] font-semibold flex items-center gap-1.5">
                <Smartphone className="w-4 h-4 text-[#d97757]" /> Ajoutez Fodindin Cagnotte à votre écran d'accueil
              </div>
              <ol className="mt-2 space-y-1.5 text-[12px] text-white/80">
                <li className="flex items-center gap-2"><span className="w-4 h-4 rounded-full bg-white/10 text-[10px] flex items-center justify-center shrink-0">1</span> Appuyez sur Partager <Share className="w-3.5 h-3.5 text-[#d97757]" /> en bas de Safari</li>
                <li className="flex items-center gap-2"><span className="w-4 h-4 rounded-full bg-white/10 text-[10px] flex items-center justify-center shrink-0">2</span> Sélectionnez « Sur l'écran d'accueil » <PlusSquare className="w-3.5 h-3.5 text-[#d97757]" /></li>
                <li className="flex items-center gap-2"><span className="w-4 h-4 rounded-full bg-white/10 text-[10px] flex items-center justify-center shrink-0">3</span> Profitez de l'app comme une vraie application !</li>
              </ol>
              <Button data-testid="pwa-ios-ok-btn" onClick={dismiss} className="btn-anim mt-3 h-9 rounded-full px-4 bg-[#d97757] hover:bg-[#c66745] text-white text-[12.5px] font-semibold">
                J'ai compris
              </Button>
            </>
          )}
        </div>
        <button data-testid="pwa-close-btn" onClick={dismiss} aria-label="Fermer" className="p-1 rounded-full hover:bg-white/10 shrink-0">
          <X className="w-4 h-4 text-white/60" />
        </button>
      </div>
    </div>
  );
}
